<template>
  <div>
    <div v-if="canManage" class="mb-3.5 flex flex-col items-end">
      <button
        type="button"
        class="flex cursor-pointer items-center gap-2 text-gray-400 hover:text-gray-500"
        @click="toggleHidden"
      >
        <EyeOpenIcon class="size-[18px]" />
        <span class="font-medium text-[14px] leading-[22px]">
          {{ isHidden ? 'Show match' : 'Hide match' }}
        </span>
      </button>

      <span
        v-if="isHidden"
        class="mt-1 text-[14px] leading-[22px] text-gray-400"
      >
        This match will not appear on a downloaded or shared report
      </span>
    </div>

    <div v-if="collapsed" class="grid grid-cols-2 gap-x-[26px] opacity-[0.36]">
      <div class="rounded-xl bg-[#d4d8e1] px-[15px] pt-2.5 pb-2">
        <div class="font-medium text-[14px] leading-[18px] text-gray-400">
          Your registered work
        </div>
        <div class="text-[13px] leading-[18px] text-gray-400">
          {{ match.copyright.location }}
        </div>
      </div>

      <div class="rounded-xl px-[15px] pt-2.5 pb-2" :class="bgClass">
        <div class="flex items-center justify-between">
          <div class="font-medium text-[14px] leading-[18px] text-white">
            Potential infringement {{ formatNumber(number) }}
          </div>

          <div class="flex items-center gap-x-3">
            <div class="font-medium text-[12px] leading-[18px] text-white">
              {{ formatNumber(match.infringingFile.score ?? 0) }}% match
            </div>

            <div class="w-[71px] h-2 bg-[#f5f8fc] rounded-md p-px">
              <div
                class="h-full rounded-md"
                :class="bgClass"
                :style="{ width: `${Number(match.infringingFile.score)}%` }"
              ></div>
            </div>
          </div>
        </div>

        <div class="text-[13px] leading-[18px] text-white">
          {{ match.infringingFile.location }}
        </div>
      </div>
    </div>

    <div v-else class="grid grid-cols-2 gap-x-[26px]">
      <div>
        <div class="w-full bg-gray-300/30 rounded-t-xl px-[15px] pt-2.5 pb-2">
          <div
            class="font-medium text-[12px] leading-[18px] px-[5px] py-px bg-gray-200 text-gray-800 rounded-md w-fit mb-1"
          >
            Registered work
          </div>

          <div class="pl-[5px] text-[13px] leading-[18px] text-gray-800">
            {{ match.copyright.location }}
          </div>
        </div>

        <div
          class="border-2 rounded-b-xl border-t-0 border-gray-300/30 px-8 pt-6 pb-8 flex flex-col gap-[22px]"
        >
          <div>
            <div class="text-[12px] leading-[16px] text-gray-500 mb-0.5">
              Registration
            </div>

            <div class="flex gap-x-1.5">
              <div class="w-5 h-5 rounded-xs bg-gray-200/25 p-px shrink-0">
                <Thumbnail
                  :type="fileTypeToThumbnail(copyrightFile.type)"
                  :src="copyrightPreviewImageUrl"
                />
              </div>

              <div class="min-w-0 break-all">
                <div
                  class="font-medium text-[13px] leading-[20px] text-gray-800 mb-[3px]"
                >
                  {{ copyrightFile.name }}
                </div>

                <div
                  v-if="showOriginalFilename"
                  class="text-[12px] leading-[18px] text-gray-800 mb-[3px]"
                >
                  Filename:
                  {{ copyrightFile.metadata.originalFilename }}
                </div>

                <RouterLink
                  :to="`/file/${copyrightFile.copyright.id}`"
                  class="text-[12px] leading-[18px] text-link"
                >
                  View registration
                </RouterLink>
              </div>
            </div>
          </div>

          <component
            :is="component"
            :match="match.copyright"
            :number="number"
            side="registered"
            :allow-playback="canPlayMatches"
          />
        </div>
      </div>

      <div>
        <div class="w-full rounded-t-xl px-[15px] pt-2.5 pb-2" :class="bgClass">
          <div class="flex items-center justify-between mb-1">
            <div
              class="font-medium text-[12px] leading-[18px] px-[5px] py-px text-white rounded-md w-fit"
              :class="pillClasses"
            >
              Potential infringement {{ formatNumber(number) }}
            </div>

            <div class="flex items-center gap-x-3">
              <div class="font-medium text-[12px] leading-[18px] text-white">
                {{ formatNumber(match.infringingFile.score) }}% match
              </div>

              <div class="w-[100px] h-2 bg-white rounded-md p-px">
                <div
                  class="h-full rounded-md"
                  :class="bgClass"
                  :style="{
                    width: `${Number(match.infringingFile.score)}%`,
                  }"
                ></div>
              </div>
            </div>
          </div>

          <div class="pl-[5px] text-[13px] leading-[18px] text-white">
            {{ match.infringingFile.location }}
          </div>
        </div>

        <div
          class="border-2 rounded-xl rounded-t-none border-t-0 px-8 pt-6 pb-8 flex flex-col gap-[22px]"
          :class="borderClass"
        >
          <div class="flex gap-x-3">
            <AlertIcon
              class="mt-0.5 size-[26px] shrink-0"
              :class="{
                'text-red-500': match.infringingFile.level === ScoreLevel.High,
                'text-yellow-500':
                  match.infringingFile.level === ScoreLevel.Medium,
                'text-green-500': match.infringingFile.level === ScoreLevel.Low,
              }"
            />
            <div class="font-medium text-[24px] leading-[30px]">
              {{ match.infringingFile.title }}
            </div>
          </div>

          <div>
            <div class="text-[12px] leading-[16px] text-gray-500 mb-0.5">
              Potential infringing work
            </div>

            <div class="flex items-center gap-x-1.5">
              <div class="w-5 h-5 rounded-xs bg-gray-200/25 p-px shrink-0">
                <Thumbnail
                  :type="fileTypeToThumbnail(infringingFile.type)"
                  :src="infringingFile.asset.previewImageUrl"
                />
              </div>
              <div class="w-full" v-truncate>
                <div
                  class="text-[12px] leading-[18px] text-gray-800"
                  data-truncate-target
                >
                  {{ infringingFile.name }}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div class="text-[12px] leading-[16px] text-gray-500 mb-1.5">
              Infringement findings
            </div>

            <div class="text-[14px] leading-[20px]">
              {{ match.infringingFile.summary }}
            </div>
          </div>

          <div class="w-full h-px bg-gray-200"></div>

          <component
            :is="component"
            :match="match.infringingFile"
            :number="number"
            side="infringing"
            :allow-playback="canPlayMatches"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const SetMatchVisibilityMutation = graphql(`
  mutation SetMatchVisibility($input: SetMatchVisibilityInput!) {
    setMatchVisibility(input: $input) {
      report {
        id
      }
    }
  }
`)

export const MatchBreakdownFragment = graphql(`
  fragment MatchBreakdown on MatchCategoryBreakdown {
    copyright {
      id
      location
      markers {
        hash
        color
        shine
        visible
      }
      file {
        ... on File {
          id
          name
          type
          asset {
            previewImageUrl
          }
          metadata {
            originalFilename
          }
          copyright {
            id
          }
        }
      }
      ... on AudioMatch {
        ...AudioMatchFields
      }
      ... on ImageMatch {
        ...ImageMatchFields
      }
      ... on TextMatch {
        ...TextMatchFields
      }
      ... on TranscriptMatch {
        ...TranscriptMatchFields
      }
      ... on VideoMatch {
        ...VideoMatchFields
      }
    }
    infringingFile {
      id
      location
      summary
      title
      score
      level
      hidden
      markers {
        hash
        color
        shine
        visible
      }
      file {
        ... on InfringingFile {
          id
          name
          type
          asset {
            previewImageUrl
          }
        }
      }
      ... on AudioMatch {
        ...AudioMatchFields
      }
      ... on ImageMatch {
        ...ImageMatchFields
      }
      ... on TextMatch {
        ...TextMatchFields
      }
      ... on TranscriptMatch {
        ...TranscriptMatchFields
      }
      ... on VideoMatch {
        ...VideoMatchFields
      }
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import {
  MatchCategoryType,
  ScoreLevel,
  type File,
  type InfringingFile,
} from '@/shared/graphql/router/graphql'
import { AlertIcon, EyeOpenIcon } from '@dacr/icons'
import { Thumbnail } from '@dacr/library'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { computed, ref, watch, type Component } from 'vue'
import AudioMatchGroup from './AudioMatchGroup.vue'
import TranscriptMatch from './TranscriptMatch.vue'
import ImageMatch from './ImageMatch.vue'
import TextMatchGroup from './TextMatchGroup.vue'
import VideoMatch from './VideoMatch.vue'
import { formatNumber } from '@/shared/utils/number'
import { useMatchVisibility } from '@/features/infringement/composables/useMatchVisibility'

const props = defineProps<{
  type: MatchCategoryType
  match: FragmentType<typeof MatchBreakdownFragment>
  number: number
  allowPlayback?: boolean
  reportId?: string
  canManage?: boolean
}>()

const match = useFragment(MatchBreakdownFragment, props.match)

const canPlayMatches = computed(() => props.allowPlayback !== false)

const { setHidden } = useMatchVisibility()

const isHidden = ref(match.infringingFile.hidden ?? false)

watch(
  () => match.infringingFile.hidden,
  (value) => (isHidden.value = value ?? false)
)

const toggleHidden = async () => {
  const reportId = props.reportId
  if (!reportId) return
  const next = !isHidden.value
  isHidden.value = next
  const ok = await setHidden(reportId, match.infringingFile.id, next)
  if (!ok) isHidden.value = !next
}

const collapsed = computed(() => isHidden.value && !!props.canManage)

const copyrightFile = computed(() => match.copyright.file as File)
const infringingFile = computed(
  () => match.infringingFile.file as InfringingFile
)
const copyrightPreviewImageUrl = computed(() =>
  applyAuthorizationToken(copyrightFile.value.asset?.previewImageUrl)
)

const component = computed<Component>(() => {
  if (props.type === MatchCategoryType.Audio) {
    return AudioMatchGroup
  }

  if (props.type === MatchCategoryType.Transcript) {
    return TranscriptMatch
  }

  if (props.type === MatchCategoryType.Text) {
    return TextMatchGroup
  }

  if (props.type === MatchCategoryType.Video) {
    return VideoMatch
  }

  return ImageMatch
})

const showOriginalFilename = computed(
  () =>
    copyrightFile.value.name !== copyrightFile.value.metadata.originalFilename
)

const pillClasses = computed(() => ({
  'bg-green-600': match.infringingFile.level === ScoreLevel.Low,
  'bg-yellow-600': match.infringingFile.level === ScoreLevel.Medium,
  'bg-red-600': match.infringingFile.level === ScoreLevel.High,
}))

const borderClass = computed(() => ({
  'border-green-500': match.infringingFile.level === ScoreLevel.Low,
  'border-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
  'border-red-500': match.infringingFile.level === ScoreLevel.High,
}))

const bgClass = computed(() => ({
  'bg-green-500': match.infringingFile.level === ScoreLevel.Low,
  'bg-yellow-500': match.infringingFile.level === ScoreLevel.Medium,
  'bg-red-500': match.infringingFile.level === ScoreLevel.High,
}))
</script>
